import { usePermissions } from 'react-admin';
import { useQuery } from 'react-query';
import {
  getAccounts,
  getAccountsLimits,
  getProfileLimits,
  getAccountsWithHeaders,
} from '~/api/accounts';
import { Account } from '~/types';

export const useGetAllAccounts = (
  params: {
    resource: 'businesses' | 'individuals' | 'partners' | 'tenant/accounts';
    profileId?: string;
    pagination?: { page: number; perPage: number };
    filters?: { [x: string]: any };
  },
  options: { enabled?: boolean; onSuccess?: (data: Account[]) => void } = {}
) => {
  const { resource, profileId, pagination, filters } = params;
  const { hasListPermission } = useAccountPermissions('accounts', resource);

  return useQuery(
    ['accounts', resource, profileId, pagination?.page, pagination?.perPage, filters],
    () => getAccounts(resource, profileId, pagination, filters),
    {
      enabled: (options?.enabled ?? true) && !!hasListPermission,
      onSuccess: options?.onSuccess,
    }
  );
};

export const useGetAllAccountsWithHeaders = (
  params: {
    resource: 'businesses' | 'individuals' | 'partners' | 'tenant/accounts';
    profileId?: string;
    pagination?: { page: number; perPage: number };
    filters?: { [x: string]: any };
  },
  options: { enabled?: boolean; onSuccess?: (data: { data: Account[]; headers: any }) => void } = {}
) => {
  const { resource, profileId, pagination, filters } = params;
  const { hasListPermission } = useAccountPermissions('accounts', resource);
  return useQuery(
    ['accountsWithHeaders', resource, profileId, pagination?.page, pagination?.perPage, filters],
    () => getAccountsWithHeaders(resource, profileId, pagination, filters),
    {
      enabled: (options?.enabled ?? true) && !!hasListPermission,
      onSuccess: options?.onSuccess,
    }
  );
};

export const useGetProfileLimits = (profileId?: string, options?: { enabled?: boolean }) => {
  const { enabled = true } = options || {};
  return useQuery(['profiles', profileId, 'limits'], () => getProfileLimits(profileId!), {
    enabled: !!profileId && enabled,
  });
};

export const useGetAccountLimits = (accountId?: string, options?: { enabled?: boolean }) => {
  const { enabled = true } = options || {};
  return useQuery(['accounts', accountId, 'limits'], () => getAccountsLimits(accountId!), {
    enabled: !!accountId && enabled,
  });
};

export function useAccountPermissions(
  type: 'accounts' | 'externalSources',
  resource?: 'businesses' | 'individuals' | 'partners' | 'tenant/accounts' | 'accounts'
) {
  const { permissions } = usePermissions();

  let hasBlockPermission = false;
  let hasUnblockPermission = false;
  let hasClosePermission = false;
  let hasAliasUpdatePermission = false;
  let hasViewPermission = false;
  let hasCreatePermission = false;
  let hasListPermission = false;

  if (type === 'accounts') {
    if (resource === 'accounts') {
      hasBlockPermission = permissions?.includes('account.block');
    } else if (resource === 'businesses') {
      hasBlockPermission = permissions?.includes('business.account.block');
    } else if (resource === 'individuals') {
      hasBlockPermission = permissions?.includes('individual.account.block');
    } else if (resource === 'partners') {
      hasBlockPermission = permissions?.includes('partner.account.block');
    } else {
      hasBlockPermission = false;
    }

    if (resource === 'accounts') {
      hasUnblockPermission = permissions?.includes('account.unblock');
    } else if (resource === 'businesses') {
      hasUnblockPermission = permissions?.includes('business.account.unblock');
    } else if (resource === 'individuals') {
      hasUnblockPermission = permissions?.includes('individual.account.unblock');
    } else if (resource === 'partners') {
      hasUnblockPermission = permissions?.includes('partner.account.unblock');
    } else {
      hasUnblockPermission = false;
    }

    if (resource === 'accounts') {
      hasClosePermission = permissions?.includes('account.close');
    } else if (resource === 'businesses') {
      hasClosePermission = permissions?.includes('business.account.close');
    } else if (resource === 'individuals') {
      hasClosePermission = permissions?.includes('individual.account.close');
    } else if (resource === 'partners') {
      hasClosePermission = permissions?.includes('partner.account.close');
    } else {
      hasClosePermission = false;
    }

    if (resource === 'accounts') {
      hasAliasUpdatePermission = permissions?.includes('account.update');
    } else if (resource === 'businesses') {
      hasAliasUpdatePermission = permissions?.includes('business.account.alias.update');
    } else if (resource === 'individuals') {
      hasAliasUpdatePermission = permissions?.includes('individual.account.alias.update');
    } else if (resource === 'partners') {
      hasAliasUpdatePermission = permissions?.includes('partner.account.alias.update');
    } else {
      hasAliasUpdatePermission = false;
    }

    if (resource === 'accounts') {
      hasViewPermission = permissions?.includes('account.view');
    } else if (resource === 'businesses') {
      hasViewPermission = permissions?.includes('business.account.view');
    } else if (resource === 'individuals') {
      hasViewPermission = permissions?.includes('individual.account.view');
    } else if (resource === 'partners') {
      hasViewPermission = permissions?.includes('partner.account.view');
    } else if (resource === 'tenant/accounts') {
      hasViewPermission = permissions?.includes('tenant.account.view');
    } else {
      hasViewPermission = false;
    }

    if (resource === 'accounts') {
      hasListPermission = permissions?.includes('account.list');
    } else if (resource === 'businesses') {
      hasListPermission = permissions?.includes('business.account.list');
    } else if (resource === 'individuals') {
      hasListPermission = permissions?.includes('individual.account.list');
    } else if (resource === 'partners') {
      hasListPermission = permissions?.includes('partner.account.list');
    } else if (resource === 'tenant/accounts') {
      hasListPermission = permissions?.includes('tenant.account.list');
    } else {
      hasListPermission = false;
    }

    if (resource === 'accounts') {
      hasCreatePermission = false; // There's no global create permission for accounts
    } else if (resource === 'businesses') {
      hasCreatePermission = permissions?.includes('business.account.create');
    } else if (resource === 'individuals') {
      hasCreatePermission = permissions?.includes('individual.account.create');
    } else if (resource === 'partners') {
      hasCreatePermission = permissions?.includes('partner.account.create');
    } else if (resource === 'tenant/accounts') {
      hasCreatePermission = permissions?.includes('tenant.account.create');
    } else {
      hasCreatePermission = false;
    }
  } else if (type === 'externalSources') {
    hasBlockPermission = permissions?.includes('externalSource.block');
    hasUnblockPermission = permissions?.includes('externalSource.unblock');
    hasClosePermission = permissions?.includes('externalSource.close');
    hasAliasUpdatePermission = permissions?.includes('externalSource.name.update');
  }
  return {
    hasBlockPermission,
    hasUnblockPermission,
    hasClosePermission,
    hasAliasUpdatePermission,
    hasViewPermission,
    hasCreatePermission,
    hasListPermission,
  };
}
